// Ogma's words, laid out as Claude lays out an answer (Craig, 31 Oct 2026: answers showed "**" and "- " as typed):
// paragraphs, bullet and numbered lists, and **bold**. Only those; everything else stays plain text, and nothing is
// read as HTML.
import type { ReactNode } from "react";

type Block = { kind: "p"; lines: string[] } | { kind: "ul" | "ol"; items: string[] };

const BULLET = /^\s*[-*•]\s+/;
const NUMBER = /^\s*\d+[.)]\s+/;

/** The answer's paragraphs and lists, in order. */
export function blocksOf(text: string): Block[] {
  const out: Block[] = [];
  for (const line of text.replace(/\r\n/g, "\n").split("\n")) {
    const last = out[out.length - 1];
    if (!line.trim()) {
      out.push({ kind: "p", lines: [] });
      continue;
    }
    const kind = BULLET.test(line) ? "ul" : NUMBER.test(line) ? "ol" : "p";
    if (kind === "p") {
      if (last?.kind === "p") last.lines.push(line);
      else out.push({ kind: "p", lines: [line] });
    } else {
      const item = line.replace(kind === "ul" ? BULLET : NUMBER, "");
      if (last?.kind === kind) last.items.push(item);
      else out.push({ kind, items: [item] });
    }
  }
  return out.filter((b) => (b.kind === "p" ? b.lines.length > 0 : true));
}

/** The words alone, on one line (a past chat's preview): no list markers or bold marks. */
export const plainOf = (text: string) =>
  text.split("\n").map((l) => l.replace(BULLET, "").replace(NUMBER, "")).join(" ").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();

/** **bold** inside a line. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean)
    .map((part, i) => (/^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> : part));
}

export function ChatText({ text }: { text: string }) {
  return (
    <div className="chat-text">
      {blocksOf(text).map((b, i) =>
        b.kind === "p" ? <p key={i}>{b.lines.map((l, j) => <span key={j}>{j > 0 && <br />}{inline(l)}</span>)}</p>
          : b.kind === "ul" ? <ul key={i}>{b.items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>
            : <ol key={i}>{b.items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ol>,
      )}
    </div>
  );
}
